/*!
 * @license
 * Copyright 2019 Alfresco Software, Ltd.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, Inject, ViewEncapsulation, SecurityContext, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';

export interface ConfirmDialogComponentProps {
    title?: string;
    message?: string;
    yesLabel?: string;
    thirdOptionLabel?: string;
    hideCancelButton?: boolean;
    noLabel?: string;
    htmlContent?: string;
    formField?: boolean;
    optionalFormField?: boolean;
    formFieldLabel?: string;
}

@Component({
    selector: 'smt-confirm-dialog',
    templateUrl: './confirm.dialog.html',
    styleUrls: ['./confirm.dialog.scss'],
    host: { class: 'smt-confirm-dialog' },
    encapsulation: ViewEncapsulation.None
})
export class SMTConfirmDialogComponent implements OnInit {

    private _formBuilder = inject(FormBuilder);
    formGroup: FormGroup<any>;

    constructor(@Inject(MAT_DIALOG_DATA) public data: ConfirmDialogComponentProps, private sanitizer: DomSanitizer, private dialogRef: MatDialogRef<SMTConfirmDialogComponent>) {
    }

    ngOnInit(): void {
        this.formGroup = this._formBuilder.group({});
        if(this.formField) {
            const validator = !this.optionalFormField ? [Validators.required] : [];
            this.formGroup.addControl('formField', this._formBuilder.control('', validator));
        }
    }

    sanitizedHtmlContent(): string {
        return this.sanitizer.sanitize(SecurityContext.HTML, this.htmlContent);
    }

    closeDialog() {
        this.dialogRef.close(this.formField ? this.formGroup.get('formField').value : true);
    }

    // GETTERS
    
    get title(): string {
        return this.data?.title || 'ALDEN.DIALOGS.CONFIRM.TITLE';
    }

    get message(): string {
        return this.data?.message || 'ALDEN.DIALOGS.CONFIRM.MESSAGE';
    }

    get yesLabel(): string {
        return this.data?.yesLabel || 'ALDEN.DIALOGS.CONFIRM.YES_LABEL';
    }

    get thirdOptionLabel(): string | undefined {
        return this.data?.thirdOptionLabel;
    }

    get noLabel(): string {
        return this.data?.noLabel || 'ALDEN.DIALOGS.CONFIRM.NO_LABEL';
    }

    get htmlContent(): string | undefined {
        return this.data?.htmlContent;
    }

    get formField(): boolean {
        return this.data?.formField || false;
    }

    get optionalFormField(): boolean {
        return this.data?.optionalFormField || false;
    }

    get formFieldLabel(): string {
        return this.data?.formFieldLabel || 'ALDEN.DIALOGS.CONFIRM.FORM_FIELD_LABEL';
    }

    get hideCancelButton(): boolean {
        return this.data?.hideCancelButton || false;
    }

}